import { onError } from '@apollo/client/link/error'

/** A save that would change a KPI question of a wave that has launched. */
export const KPI_SUBJECT_LOCKED = 'KPI_SUBJECT_LOCKED'
/** A change refused because its wave has left draft. */
export const WAVE_NOT_DRAFT = 'WAVE_NOT_DRAFT'
/** The same refusal from a server that sends it without a code. */
export const WAVE_NOT_DRAFT_MATCH = /has left draft/
/** A save refused because the lock could not read the wave's state; retrying may work. */
export const KPI_LOCK_UNAVAILABLE = 'KPI_LOCK_UNAVAILABLE'
/** KPI_LOCK_UNAVAILABLE for a wave whose saved questionnaire can't be read; retrying won't help. */
export const KPI_LOCK_DRAFT_UNREADABLE = 'KPI_LOCK_DRAFT_UNREADABLE'
export const KPI_LOCK_DRAFT_UNREADABLE_MATCH =
  /saved questionnaire can't be read/

/** The error's code, with KPI_LOCK_UNAVAILABLE narrowed by its message to KPI_LOCK_DRAFT_UNREADABLE. */
export const refusalCodeOf = ({
  extensions,
  message
}: {
  extensions?: unknown
  message: string
}): unknown => {
  // Errors arrive as the server sends them, so one without a code has no extensions at all.
  const code = (extensions as Record<string, unknown> | undefined)?.code
  return code === KPI_LOCK_UNAVAILABLE &&
    KPI_LOCK_DRAFT_UNREADABLE_MATCH.test(message)
    ? KPI_LOCK_DRAFT_UNREADABLE
    : code
}

// Precedence order: a lock refusal outranks a left-draft one, and both outrank a lock that couldn't check.
const REFUSALS = [
  KPI_SUBJECT_LOCKED,
  WAVE_NOT_DRAFT,
  KPI_LOCK_DRAFT_UNREADABLE,
  KPI_LOCK_UNAVAILABLE
] as const

export type WaveLockRefusal = (typeof REFUSALS)[number]

type Listener = (
  refusal: WaveLockRefusal,
  waveId?: string,
  entryId?: string
) => void

const listeners = new Set<Listener>()

export const onWaveLockRefusal = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const idOf = (
  variables: Record<string, unknown>,
  key: 'waveId' | 'entryId'
) => {
  const candidates = [variables.input, variables, ...Object.values(variables)]
  const id = candidates
    .map((value) =>
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)[key]
        : undefined
    )
    .find((value) => typeof value === 'string')
  return typeof id === 'string' ? id : undefined
}

// Any questionnaire save can meet the lock, so refusals are caught here rather than by each mutation.
export const waveLockLink = onError(({ graphQLErrors = [], operation }) => {
  const codes = graphQLErrors.flatMap(({ extensions, message }) => {
    const code = refusalCodeOf({ extensions, message })
    return WAVE_NOT_DRAFT_MATCH.test(message) ? [code, WAVE_NOT_DRAFT] : [code]
  })
  const refusal = REFUSALS.find((code) => codes.includes(code))
  if (refusal) {
    const waveId = idOf(operation.variables, 'waveId')
    const entryId = idOf(operation.variables, 'entryId')
    listeners.forEach((listener) => listener(refusal, waveId, entryId))
  }
})
